/**
 * Change Log — Metadata for undo history entries.
 *
 * Each entry records a single property change (or an add/remove operation)
 * with enough context for the history UI to display, filter, and
 * selectively revert changes.
 */
import { CORE_CODEPOINT_INDEX_KEY, CORE_GLYPH_CATALOG_KEY } from './filesystem-plugins/cloud-glyph-catalog';
/** Edit sources stamped on Python / Assistant font mutations */
export const AUTOMATION_EDIT_SOURCES = new Set(['python', 'assistant']);
/**
 * True when a history item was produced by Scripts, Konsole, or Assistant
 * Python execution (including assistant prompt-grouped packets).
 */
export function isAutomationSourcedHistoryItem(item) {
    if (item.transactionLabel === 'Python script') {
        return true;
    }
    return item.entries.some((entry) => {
        if (entry.promptGroupId) {
            return true;
        }
        if (entry.transactionLabel === 'Python script') {
            return true;
        }
        const source = entry.editSource ?? entry.compileChangeSource ?? null;
        return !!source && AUTOMATION_EDIT_SOURCES.has(source);
    });
}
function isAutomationEditSource(editSource, compileChangeSource) {
    const source = editSource ?? compileChangeSource ?? null;
    return !!source && AUTOMATION_EDIT_SOURCES.has(source);
}
/**
 * Font-root paths that the Editing View commonly writes (kerning / kern groups).
 * Used as a headless fallback when no focused undo surface is available.
 */
export function isEditorOwnedFontPath(path) {
    if (!path) {
        return false;
    }
    if (path === 'first_kern_groups' || path.startsWith('first_kern_groups.')) {
        return true;
    }
    if (path === 'second_kern_groups' ||
        path.startsWith('second_kern_groups.')) {
        return true;
    }
    return /^masters\.[^.]+\.kerning(_rtl)?(\.|$)/.test(path);
}
/**
 * Which non-Font undo surface should own this font-scoped (or mixed) edit.
 * Automation and feature targets stay on their dedicated surfaces.
 */
export function resolveUndoSurfaceAffinity(options) {
    if (options.historyTargetKey) {
        return null;
    }
    if (options.transactionLabel === 'Python script' ||
        isAutomationEditSource(options.editSource, options.compileChangeSource)) {
        return null;
    }
    if (options.explicit === 'canvas' || options.explicit === 'overview') {
        return options.explicit;
    }
    if (options.contextSurface === 'canvas' ||
        options.contextSurface === 'overview') {
        return options.contextSurface;
    }
    // Font / feature / automation context: keep affinity unset so those
    // surfaces own the item via their normal matchers.
    if (options.contextSurface) {
        return null;
    }
    const paths = options.paths ?? [];
    if (paths.length > 0 &&
        paths.every((path) => isEditorOwnedFontPath(path))) {
        return 'canvas';
    }
    return null;
}
export function deriveOriginatingLayerFromPaths(paths) {
    for (const path of paths) {
        const glyphName = deriveGlyphNameFromPath(path);
        const layerId = deriveLayerIdFromPath(path);
        if (glyphName && layerId) {
            return { glyphName, layerId };
        }
    }
    return { glyphName: null, layerId: null };
}
function operationPathTouchesLayer(path) {
    return !!path && !!deriveLayerId(path);
}
/**
 * Transaction undo origin: the layer the user edited, not each written
 * cascade/replay target. Canvas context wins for layer-touching commits so
 * dependent snapshots cannot claim ownership. Font-root canvas edits (kerning)
 * leave origin unset.
 */
export function resolveCommitOriginatingLayer(options) {
    const operations = options.operations ?? [];
    const touchesLayer = operations.some((operation) => operationPathTouchesLayer(operation.applyPath) ||
        operationPathTouchesLayer(operation.path));
    if (options.contextSurface === 'canvas' &&
        options.contextGlyphName &&
        options.contextLayerId &&
        touchesLayer) {
        return {
            glyphName: options.contextGlyphName,
            layerId: options.contextLayerId
        };
    }
    for (const operation of operations) {
        if (operation.originatingGlyphName && operation.originatingLayerId) {
            return {
                glyphName: operation.originatingGlyphName,
                layerId: operation.originatingLayerId
            };
        }
    }
    return deriveOriginatingLayerFromPaths(operations.map((operation) => {
        const path = operation.applyPath ?? operation.path ?? [];
        return joinPathWithGlyphSeparator(path);
    }));
}
/**
 * Originating layer for collaboration / history display metadata.
 *
 * Only layer-scoped edits invent an origin from paths. Font- and glyph-scoped
 * packets often include many layer paths as structural payloads (add/remove
 * master, glyph paste cascades); those must not become a fake Layer origin.
 */
export function resolveCollaborationOriginatingLayer(undoScope, entries) {
    for (const entry of entries) {
        if (entry.originatingGlyphName && entry.originatingLayerId) {
            return {
                glyphName: entry.originatingGlyphName,
                layerId: entry.originatingLayerId
            };
        }
    }
    if (undoScope !== 'layer') {
        return { glyphName: null, layerId: null };
    }
    return deriveOriginatingLayerFromPaths(entries.map((entry) => entry.path));
}
function collectTouchedLayerIdsForGlyph(paths, glyphName) {
    const layerIds = new Set();
    for (const path of paths) {
        if (deriveGlyphNameFromPath(path) !== glyphName) {
            continue;
        }
        const layerId = deriveLayerIdFromPath(path);
        if (layerId) {
            layerIds.add(layerId);
        }
    }
    return layerIds;
}
/**
 * History row origin subtitle: which undo surface owns this edit.
 * Prefers stamped undoScope over path-inferred layer identity.
 */
export function formatHistoryOriginLabel(options) {
    if (options.historyTargetKey || options.historyTargetLabel) {
        return `Feature · ${options.historyTargetLabel || options.historyTargetKey}`;
    }
    if (options.undoSurfaceAffinity === 'canvas') {
        const layerOriginLabel = formatLayerHistoryOriginLabel(options);
        if (layerOriginLabel) {
            return layerOriginLabel;
        }
        return 'Editing View';
    }
    if (options.undoSurfaceAffinity === 'overview') {
        return 'Overview';
    }
    if (options.undoScope === 'font') {
        return 'Font';
    }
    if (options.undoScope === 'glyph') {
        const layerOriginLabel = formatLayerHistoryOriginLabel(options);
        if (layerOriginLabel) {
            return layerOriginLabel;
        }
        return 'Overview';
    }
    return formatLayerHistoryOriginLabel(options) ?? 'Font';
}
function formatLayerHistoryOriginLabel(options) {
    const pathOrigin = deriveOriginatingLayerFromPaths(options.changePaths ?? []);
    const originatingGlyph = options.originatingGlyphName ?? pathOrigin.glyphName;
    const originatingLayer = options.originatingLayerId ?? pathOrigin.layerId;
    if (!originatingGlyph || !originatingLayer) {
        return null;
    }
    const touchedLayerIds = collectTouchedLayerIdsForGlyph(options.changePaths ?? [], originatingGlyph);
    if (touchedLayerIds.size > 1) {
        return `Layer · ${originatingGlyph}`;
    }
    const layerLabel = options.resolveLayerMasterDisplayName
        ? options.resolveLayerMasterDisplayName(originatingGlyph, originatingLayer)
        : originatingLayer;
    return `Layer · ${originatingGlyph} / ${layerLabel}`;
}
export function normalizeGlyphRenames(renames) {
    if (!renames)
        return [];
    const normalized = new Map();
    for (const rename of renames) {
        if (!rename?.oldName ||
            !rename.newName ||
            rename.oldName === rename.newName) {
            continue;
        }
        normalized.set(rename.oldName, {
            oldName: rename.oldName,
            newName: rename.newName
        });
    }
    return [...normalized.values()];
}
/**
 * Adapt stored forward renames for undo/redo worker metadata.
 * Undo inverts identity so apply_yjs_update resolves post-undo Y.Doc snapshots.
 */
export function glyphRenamesForHistoryAction(renames, historyAction) {
    const normalized = normalizeGlyphRenames(renames);
    if (historyAction !== 'undo') {
        return normalized;
    }
    return normalizeGlyphRenames(normalized.map(({ oldName, newName }) => ({
        oldName: newName,
        newName: oldName
    })));
}
export function getLayerTouchKey(glyphName, layerId) {
    if (!glyphName || !layerId) {
        return null;
    }
    return `${glyphName}@@${layerId}`;
}
export function normalizeWorkerReplayTargets(targets) {
    if (!targets) {
        return [];
    }
    const normalizedTargets = new Map();
    for (const target of targets) {
        if (!target?.glyphName || !target?.layerId) {
            continue;
        }
        normalizedTargets.set(getLayerTouchKey(target.glyphName, target.layerId), {
            glyphName: target.glyphName,
            layerId: target.layerId
        });
    }
    return [...normalizedTargets.values()];
}
let _nextId = 1;
/** Create a new ChangeLogEntry with an auto-incremented id. */
export function createLogEntry(fields) {
    const nextId = _nextId++;
    const glyphName = deriveGlyphNameFromPath(fields.path);
    const layerId = deriveLayerIdFromPath(fields.path);
    return attachDerivedEntryAccessors({
        id: nextId,
        historyItemId: fields.historyItemId ?? `history-item-${nextId}`,
        historyAction: fields.historyAction ?? 'change',
        targetHistoryItemId: fields.targetHistoryItemId ?? null,
        undoScope: fields.undoScope ?? deriveUndoScope(glyphName, layerId),
        undoSurfaceAffinity: fields.undoSurfaceAffinity ?? null,
        timestamp: fields.timestamp,
        transactionDurationMs: fields.transactionDurationMs ?? null,
        windowId: fields.windowId,
        windowRoleLabel: fields.windowRoleLabel,
        transactionLabel: fields.transactionLabel,
        transactionId: fields.transactionId,
        op: fields.op,
        path: fields.path,
        oldValue: fields.oldValue,
        newValue: fields.newValue,
        replayOldValue: fields.replayOldValue,
        replayNewValue: fields.replayNewValue,
        editSource: fields.editSource ?? null,
        compileChangeSource: fields.compileChangeSource ?? null,
        compileEditType: fields.compileEditType ?? null,
        historySummary: fields.historySummary ?? null,
        promptGroupId: fields.promptGroupId ?? null,
        historyTargetType: fields.historyTargetType ?? null,
        historyTargetKey: fields.historyTargetKey ?? null,
        historyTargetLabel: fields.historyTargetLabel ?? null,
        originatingGlyphName: fields.originatingGlyphName ?? null,
        originatingLayerId: fields.originatingLayerId ?? null,
        visualAnchorSide: fields.visualAnchorSide ?? null,
        workerReplayTargets: normalizeWorkerReplayTargets(fields.workerReplayTargets),
        glyphRenames: normalizeGlyphRenames(fields.glyphRenames),
        semanticChangeLogEntries: fields.semanticChangeLogEntries ?? undefined
    });
}
const FONT_SCOPE_KEY = '__font__';
function getGlyphScopeKey(glyphName) {
    return glyphName ?? FONT_SCOPE_KEY;
}
export function isDerivedLayerChangePath(path, originatingGlyphName) {
    if (!originatingGlyphName) {
        return false;
    }
    const glyphName = deriveGlyphNameFromPath(path);
    return !!glyphName && glyphName !== originatingGlyphName;
}
function getLayerScopeKey(glyphName, layerId) {
    return getLayerTouchKey(glyphName, layerId);
}
/**
 * Join path segments using a deterministic glyph-name separator.
 *
 * The glyph name (path[1]) is separated from the rest of the path by ':'
 * so that downstream parsing can extract the glyph name in O(1) without
 * consulting a glyph-name list.  All other segments use '.'.
 *
 * Only applies ':' when path[0] is 'glyphs'. Other roots escape dots
 * inside a segment so keys such as `com.counterpunch.plugin-settings`
 * survive `getPathSegments`.
 */
function encodePathSegment(segment) {
    return String(segment).replace(/\\/g, '\\\\').replace(/\./g, '\\.');
}
function encodePathTail(segments) {
    return segments.map(encodePathSegment).join('.');
}
/** Split on dots that are not escaped by a preceding backslash. */
function decodePathTail(tail) {
    if (!tail) {
        return [];
    }
    const segments = [];
    let current = '';
    for (let index = 0; index < tail.length; index++) {
        const char = tail[index];
        if (char === '\\' && index + 1 < tail.length) {
            current += tail[index + 1];
            index++;
            continue;
        }
        if (char === '.') {
            segments.push(current);
            current = '';
            continue;
        }
        current += char;
    }
    segments.push(current);
    return segments;
}
export function joinPathWithGlyphSeparator(path) {
    if (path.length === 0)
        return '';
    if (path[0] !== 'glyphs')
        return encodePathTail(path);
    if (path.length === 1)
        return 'glyphs';
    // Glyph-root add/remove paths must terminate the name with ':' so dotted
    // names like fourFarsi-ar.locl round-trip through getPathSegments.
    if (path.length === 2) {
        return `${path[0]}.${path[1]}:`;
    }
    // Glyph-level fields are not layer ids. A trailing colon here makes
    // format_specific.metric_left look like a layer named metric_left.
    if (path[2] !== 'layers') {
        return `${path[0]}.${path[1]}:${encodePathTail(path.slice(2))}`;
    }
    // Separator after glyph name
    let result = path[0] + '.' + path[1] + ':' + path[2];
    if (path.length <= 3)
        return result;
    // Separator after layer ID (path[3]). Later segments may contain dots
    // (Glyphs metrics-key names), so those dots are escaped.
    result += '.' + path[3] + ':' + encodePathTail(path.slice(4));
    return result;
}
/**
 * Split a change-path string that uses ':' as the separator at glyph-name
 * and layer-ID boundaries.
 *
 * Format: glyphs.{glyphName}:layers.{layerId}:shapes.0.nodes.2.x
 * (glyph names and layer IDs may contain dots — ':' is unambiguous.)
 */
function splitGlyphPath(path) {
    if (!path.startsWith('glyphs.')) {
        return null;
    }
    const rest = path.slice('glyphs.'.length);
    const colonIdx = rest.indexOf(':');
    if (colonIdx < 0) {
        // Legacy path without ':' — fall back to dot-splitting.
        return path.split('.');
    }
    const glyphName = rest.slice(0, colonIdx);
    const afterGlyph = rest.slice(colonIdx + 1);
    if (!afterGlyph)
        return ['glyphs', glyphName];
    // Split layer-id boundary: layers.{layerId}:rest
    const secondColonIdx = afterGlyph.indexOf(':');
    if (secondColonIdx < 0) {
        // No layer separator — remainder is a glyph-level field path.
        return ['glyphs', glyphName, ...decodePathTail(afterGlyph)];
    }
    const layerPart = afterGlyph.slice(0, secondColonIdx); // "layers.layer.regular.v1"
    const afterLayer = afterGlyph.slice(secondColonIdx + 1); // "shapes.0.nodes.2.x"
    const layerSegments = layerPart.split('.');
    // layerSegments[0] should be "layers", the rest is the layer ID
    const layerId = layerSegments.slice(1).join('.');
    const segments = ['glyphs', glyphName, 'layers', layerId];
    if (!afterLayer)
        return segments;
    return [...segments, ...decodePathTail(afterLayer)];
}
export function getPathSegments(path) {
    if (!path || path === 'font') {
        return [];
    }
    return splitGlyphPath(path) || decodePathTail(path);
}
function derivePropertyFromPath(path) {
    const segments = getPathSegments(path);
    if (!segments.length) {
        return '';
    }
    const lastSegment = segments[segments.length - 1];
    if (typeof lastSegment !== 'string') {
        return '';
    }
    const fullObject = deriveObjectInfo(segments);
    const parentObject = deriveObjectInfo(segments.slice(0, -1));
    if (fullObject.objectType !== parentObject.objectType ||
        fullObject.objectId !== parentObject.objectId) {
        return '';
    }
    return lastSegment;
}
function attachDerivedEntryAccessors(entry) {
    Object.defineProperties(entry, {
        glyphName: {
            configurable: true,
            enumerable: false,
            get: () => deriveGlyphNameFromPath(entry.path)
        },
        layerId: {
            configurable: true,
            enumerable: false,
            get: () => deriveLayerIdFromPath(entry.path)
        },
        objectType: {
            configurable: true,
            enumerable: false,
            get: () => deriveObjectInfoFromPath(entry.path).objectType
        },
        objectId: {
            configurable: true,
            enumerable: false,
            get: () => deriveObjectInfoFromPath(entry.path).objectId
        },
        property: {
            configurable: true,
            enumerable: false,
            get: () => derivePropertyFromPath(entry.path)
        },
        touchedPaths: {
            configurable: true,
            enumerable: false,
            get: () => [entry.path]
        },
        touchedGlyphNames: {
            configurable: true,
            enumerable: false,
            get: () => deriveGlyphNamesFromPaths([entry.path])
        },
        touchedLayerKeys: {
            configurable: true,
            enumerable: false,
            get: () => deriveLayerTouchKeysFromPaths([entry.path])
        }
    });
    return entry;
}
function normalizeHistoryAction(historyAction) {
    if (historyAction === 'change' ||
        historyAction === 'undo' ||
        historyAction === 'redo') {
        return historyAction;
    }
    return 'change';
}
function normalizeHistoryItemId(historyItemId, entryId) {
    if (historyItemId?.trim()) {
        return historyItemId.trim();
    }
    return `history-item-${entryId}`;
}
function getStack(map, scopeKey) {
    let stack = map.get(scopeKey);
    if (!stack) {
        stack = [];
        map.set(scopeKey, stack);
    }
    return stack;
}
function removeFromStack(stack, itemId) {
    const index = stack.lastIndexOf(itemId);
    if (index >= 0) {
        stack.splice(index, 1);
    }
}
function deriveUndoScope(glyphName, layerId) {
    if (!glyphName) {
        return 'font';
    }
    if (layerId) {
        return 'layer';
    }
    return 'glyph';
}
function deriveHistoryItemUndoScope(entries, glyphNames, layerIds, originatingGlyph, originatingLayerId) {
    if (entries.some((entry) => entry.undoScope === 'font')) {
        return 'font';
    }
    if (glyphNames.size === 0) {
        return 'font';
    }
    if (glyphNames.size > 1) {
        // Layer edits that cascade into dependent glyphs stay on the
        // originating layer's stack.
        return originatingGlyph &&
            originatingLayerId &&
            entries.every((entry) => entry.undoScope === 'layer' &&
                (deriveGlyphNameFromPath(entry.path) !== originatingGlyph ||
                    deriveLayerIdFromPath(entry.path) ===
                        originatingLayerId)) &&
            !historyItemHasFontStructuralPath(entries)
            ? 'layer'
            : 'font';
    }
    if (entries.some((entry) => entry.undoScope === 'glyph')) {
        return 'glyph';
    }
    if (layerIds.size !== 1) {
        return 'glyph';
    }
    return 'layer';
}
/**
 * True for font-root edits (masters, axes, names, …). Layer paths under
 * `glyphs.*` are not structural even when the history item is font-scoped
 * for undo (e.g. sidebearing cascades).
 */
function isFontStructuralChangePath(path) {
    return (typeof path === 'string' &&
        path.length > 0 &&
        !path.startsWith('glyphs.'));
}
/**
 * Cloud catalog rows are room bookkeeping rewritten on save and open. A
 * one-glyph undo must never revert them, and replaying the whole catalog
 * exceeds the room's collaboration metadata limit.
 */
function isCloudCatalogPath(path) {
    const root = getPathSegments(path)[0];
    return root === CORE_GLYPH_CATALOG_KEY || root === CORE_CODEPOINT_INDEX_KEY;
}
function historyItemHasFontStructuralPath(entries) {
    return entries.some((entry) => isFontStructuralChangePath(entry.path));
}
function stripMutableHistoryItem(item) {
    // Materialize the user-visible array views once here, after every
    // entry for this item has been folded into the underlying Set/Map.
    // Materializing inside the per-entry loop in `computeHistoryState`
    // is O(N²) per item: each new path / target key / replay target
    // would re-spread the entire collection. For anchor cascades that
    // attach 30-50 replay targets per entry to history items spanning
    // 40+ entries, this dominated commit time and produced the long
    // freeze observed when switching outline→anchor→outline. See
    // COMPILATION_EDIT_POLICY.md.
    item.touchedPaths = [...item.touchedPathSet];
    item.historyTargetKeys = [...item.historyTargetKeySet];
    item.workerReplayTargets = [...item.workerReplayTargetMap.values()];
    const { glyphNameSet: _glyphNameSet, layerIdSet: _layerIdSet, touchedPathSet: _touchedPathSet, touchedLayerKeySet: _touchedLayerKeySet, scopeKeys: _scopeKeys, historyTargetKeySet: _historyTargetKeySet, workerReplayTargetMap: _workerReplayTargetMap, ...historyItem } = item;
    return historyItem;
}
function makeEmptyHistoryState() {
    return {
        orderedItemIds: [],
        itemsById: new Map(),
        activeByScope: new Map(),
        undoneByScope: new Map()
    };
}
/**
 * Value history replay writes for `direction`. When either replay payload is
 * present both are authoritative; `oldValue`/`newValue` may be display labels.
 */
export function historyReplayValue(entry, direction) {
    const hasReplayPayload = entry.replayOldValue !== undefined ||
        entry.replayNewValue !== undefined;
    if (direction === 'undo') {
        return hasReplayPayload ? entry.replayOldValue : entry.oldValue;
    }
    return hasReplayPayload ? entry.replayNewValue : entry.newValue;
}
/** Glyph roots (`glyphs.A`) and layer roots (`glyphs.A:layers.L`). */
export function isSnapshotRootPath(path) {
    const segments = getPathSegments(path);
    return (segments[0] === 'glyphs' &&
        (segments.length === 2 ||
            (segments.length === 4 && segments[2] === 'layers')));
}
/**
 * A forward entry can be replayed when it carries the value its direction
 * needs (`set` needs either side; `add`/`remove` carry one side and delete
 * on the other). Snapshot roots only replay objects (null/undefined means
 * absent): metadata-only layer packets carry label strings there instead.
 */
export function isReplayableHistoryEntry(entry) {
    const oldValue = historyReplayValue(entry, 'undo');
    const newValue = historyReplayValue(entry, 'redo');
    if (entry.op === 'add') {
        return newValue !== undefined;
    }
    if (entry.op === 'remove') {
        return oldValue !== undefined;
    }
    if (isSnapshotRootPath(entry.path)) {
        const isSnapshot = (value) => value === undefined || value === null || typeof value === 'object';
        return (isSnapshot(oldValue) &&
            isSnapshot(newValue) &&
            (!!oldValue || !!newValue));
    }
    return oldValue !== undefined || newValue !== undefined;
}
function processHistoryEntry(entry, state, seq) {
    const { orderedItemIds, itemsById, activeByScope, undoneByScope } = state;
    const ensureItem = (entry) => {
        let item = itemsById.get(entry.historyItemId);
        if (!item) {
            item = {
                id: entry.historyItemId,
                entries: [],
                timestamp: entry.timestamp,
                transactionDurationMs: entry.transactionDurationMs ?? null,
                windowRoleLabel: entry.windowRoleLabel,
                transactionLabel: entry.transactionLabel,
                historySummary: entry.historySummary ?? null,
                undoScope: entry.undoScope,
                undoSurfaceAffinity: entry.undoSurfaceAffinity ?? null,
                touchedPaths: [],
                glyphNameSet: new Set(),
                layerIdSet: new Set(),
                touchedPathSet: new Set(),
                touchedLayerKeySet: new Set(),
                scopeKeys: new Set(),
                historyTargetKeySet: new Set(),
                workerReplayTargetMap: new Map(),
                isActive: true,
                lastAction: 'change',
                historyTargetKeys: [],
                workerReplayTargets: [],
                originatingGlyphName: null,
                originatingLayerId: null,
                createdSeq: seq,
                transitionSeq: seq,
                replayable: false
            };
            itemsById.set(entry.historyItemId, item);
            orderedItemIds.push(entry.historyItemId);
        }
        return item;
    };
    const entryGlyphName = deriveGlyphNameFromPath(entry.path);
    const entryLayerId = deriveLayerIdFromPath(entry.path);
    const glyphScopeKey = getGlyphScopeKey(entryGlyphName);
    if (entry.historyAction === 'change') {
        if (isCloudCatalogPath(entry.path)) {
            return;
        }
        const item = ensureItem(entry);
        item.entries.push(entry);
        item.timestamp = entry.timestamp;
        item.transactionDurationMs =
            entry.transactionDurationMs ?? item.transactionDurationMs;
        item.windowRoleLabel = entry.windowRoleLabel;
        item.transactionLabel = entry.transactionLabel;
        item.historySummary = entry.historySummary ?? item.historySummary;
        item.isActive = true;
        item.lastAction = 'change';
        item.transitionSeq = seq;
        if (!item.replayable && isReplayableHistoryEntry(entry)) {
            item.replayable = true;
        }
        if (!item.undoSurfaceAffinity &&
            (entry.undoSurfaceAffinity === 'canvas' ||
                entry.undoSurfaceAffinity === 'overview')) {
            item.undoSurfaceAffinity = entry.undoSurfaceAffinity;
        }
        if (entryGlyphName && !item.glyphNameSet.has(entryGlyphName)) {
            item.glyphNameSet.add(entryGlyphName);
        }
        if (entryLayerId && !item.layerIdSet.has(entryLayerId)) {
            item.layerIdSet.add(entryLayerId);
        }
        if (!item.originatingGlyphName &&
            !item.originatingLayerId &&
            entry.originatingGlyphName &&
            entry.originatingLayerId) {
            item.originatingGlyphName = entry.originatingGlyphName;
            item.originatingLayerId = entry.originatingLayerId;
        }
        if (!item.originatingGlyphName &&
            !item.originatingLayerId &&
            entryGlyphName &&
            entryLayerId) {
            // First layer path in the transaction is the edit origin (cascades
            // append dependents after the source write).
            item.originatingGlyphName = entryGlyphName;
            item.originatingLayerId = entryLayerId;
        }
        if (!item.touchedPathSet.has(entry.path)) {
            item.touchedPathSet.add(entry.path);
        }
        const touchedLayerKey = getLayerTouchKey(entryGlyphName, entryLayerId);
        if (touchedLayerKey && !item.touchedLayerKeySet.has(touchedLayerKey)) {
            item.touchedLayerKeySet.add(touchedLayerKey);
        }
        if (entry.historyTargetKey &&
            !item.historyTargetKeySet.has(entry.historyTargetKey)) {
            item.historyTargetKeySet.add(entry.historyTargetKey);
        }
        for (const target of normalizeWorkerReplayTargets(entry.workerReplayTargets)) {
            const targetKey = getLayerTouchKey(target.glyphName, target.layerId);
            if (targetKey && !item.workerReplayTargetMap.has(targetKey)) {
                item.workerReplayTargetMap.set(targetKey, target);
            }
        }
        item.undoScope = deriveHistoryItemUndoScope(item.entries, item.glyphNameSet, item.layerIdSet, item.originatingGlyphName, item.originatingLayerId);
        // Master add/remove (and similar) mix a font-root write with many
        // glyph layer paths. Keep them on the Font undo surface — do not
        // attribute the transaction to the first touched layer, or History
        // labels them "Layer" and Font-context undo cannot reach them.
        if (historyItemHasFontStructuralPath(item.entries)) {
            item.originatingGlyphName = null;
            item.originatingLayerId = null;
        }
        if (!item.scopeKeys.has(glyphScopeKey)) {
            item.scopeKeys.add(glyphScopeKey);
            getStack(activeByScope, glyphScopeKey).push(item.id);
        }
        const layerScopeKey = getLayerScopeKey(entryGlyphName, entryLayerId);
        if (layerScopeKey &&
            !item.scopeKeys.has(layerScopeKey) &&
            !historyItemHasFontStructuralPath(item.entries)) {
            item.scopeKeys.add(layerScopeKey);
            getStack(activeByScope, layerScopeKey).push(item.id);
        }
        getStack(undoneByScope, glyphScopeKey).length = 0;
        if (layerScopeKey && !historyItemHasFontStructuralPath(item.entries)) {
            getStack(undoneByScope, layerScopeKey).length = 0;
        }
        return;
    }
    const sourceMap = entry.historyAction === 'undo' ? activeByScope : undoneByScope;
    const targetMap = entry.historyAction === 'undo' ? undoneByScope : activeByScope;
    const sourceStack = getStack(sourceMap, glyphScopeKey);
    const targetStack = getStack(targetMap, glyphScopeKey);
    const targetItemId = entry.targetHistoryItemId ?? sourceStack[sourceStack.length - 1];
    if (!targetItemId) {
        return;
    }
    const item = itemsById.get(targetItemId);
    if (item) {
        const itemScopeKeys = item.scopeKeys.size > 0 ? [...item.scopeKeys] : [glyphScopeKey];
        for (const scopeKey of itemScopeKeys) {
            removeFromStack(getStack(sourceMap, scopeKey), targetItemId);
            getStack(targetMap, scopeKey).push(targetItemId);
        }
        item.isActive = entry.historyAction === 'redo';
        item.lastAction = entry.historyAction;
        item.timestamp = entry.timestamp;
        item.transitionSeq = seq;
    }
    else {
        removeFromStack(sourceStack, targetItemId);
        targetStack.push(targetItemId);
    }
}
/**
 * Cache of computed history state, keyed by the change-log array reference.
 * Because `_changeLog` is append-only between resets (see change-bridge.ts),
 * we can incrementally fold only the new tail entries into the cached state
 * on subsequent calls. Resetting `_changeLog` reassigns the array and the
 * WeakMap entry naturally falls out, forcing a fresh computation.
 *
 * Without this cache, every history-view re-render walks the entire change
 * log from scratch (O(N) per render). On long sessions with anchor cascades,
 * this dominated commit time and produced the long freeze observed when
 * switching outline\u2192anchor\u2192outline. See COMPILATION_EDIT_POLICY.md.
 */
const historyStateCache = new WeakMap();
export function invalidateHistoryStateCache(entries) {
    historyStateCache.delete(entries);
}
function computeHistoryState(entries) {
    let cached = historyStateCache.get(entries);
    if (!cached || cached.processedLength > entries.length) {
        cached = { processedLength: 0, state: makeEmptyHistoryState() };
        historyStateCache.set(entries, cached);
    }
    for (let i = cached.processedLength; i < entries.length; i++) {
        processHistoryEntry(entries[i], cached.state, i);
    }
    cached.processedLength = entries.length;
    return cached.state;
}
export function resolveHistoryTargetItemId(entries, glyphName, historyAction, layerId) {
    return (resolveHistoryTargetItem(entries, {
        glyphName,
        layerId,
        historyAction
    })?.id ?? null);
}
export function resolveHistoryTargetItem(entries, options) {
    const glyphName = options.glyphName ?? null;
    const layerId = options.layerId ?? null;
    const historyTargetKey = options.historyTargetKey ?? null;
    return selectHistoryTarget(buildHistoryStackItems(entries, {
        glyphName,
        layerId,
        includeUndone: true,
        historyTargetKey,
        surface: options.surface ?? null
    }), options.historyAction);
}
function latestCreatedSeq(items) {
    let latest = -1;
    for (const item of items) {
        if (item.replayable && item.createdSeq > latest) {
            latest = item.createdSeq;
        }
    }
    return latest;
}
/** An undone item stays redoable until a newer edit lands on the same surface. */
function isDiscardedRedoBranch(item, latestCreated) {
    return !item.isActive && item.transitionSeq < latestCreated;
}
/**
 * One LIFO stack per surface: undo takes the most recently applied
 * (changed or redone) item, redo the most recently undone one.
 */
export function selectHistoryTarget(surfaceItems, historyAction) {
    const latestCreated = latestCreatedSeq(surfaceItems);
    let selected = null;
    for (const item of surfaceItems) {
        if (!item.replayable) {
            continue;
        }
        if (historyAction === 'undo' ? !item.isActive : item.isActive) {
            continue;
        }
        if (historyAction === 'redo' &&
            isDiscardedRedoBranch(item, latestCreated)) {
            continue;
        }
        if (!selected || item.transitionSeq > selected.transitionSeq) {
            selected = item;
        }
    }
    return selected;
}
/**
 * History item ids that belong to the current surface’s undo/redo stack.
 * Includes undone items that are still redoable so they stay bright in
 * History; redo branches discarded by a newer edit are unreachable.
 */
export function getUndoReachabilityForContext(entries, options = {}) {
    const filterOptions = {
        glyphName: options.glyphName ?? null,
        layerId: options.layerId ?? null,
        historyTargetKey: options.historyTargetKey ?? null,
        surface: options.surface ?? null
    };
    const surfaceItems = buildHistoryStackItems(entries, {
        ...filterOptions,
        includeUndone: true
    });
    const latestCreated = latestCreatedSeq(surfaceItems);
    return {
        reachableHistoryItemIds: new Set(surfaceItems
            .filter((item) => item.replayable &&
            !isDiscardedRedoBranch(item, latestCreated))
            .map((item) => item.id)),
        nextUndoHistoryItemId: selectHistoryTarget(surfaceItems, 'undo')?.id ?? null
    };
}
function inferHistoryUndoSurface(options) {
    if (options.surface) {
        return options.surface;
    }
    if (options.historyTargetKey) {
        return 'feature';
    }
    if (options.glyphName && options.layerId) {
        return 'canvas';
    }
    if (options.glyphName) {
        return 'overview';
    }
    // No surface / glyph / layer / feature key → unfiltered list (tests, dumps).
    return null;
}
/**
 * Canvas Cmd+Z includes every layer of the originating glyph that this item
 * actually wrote. Dependent glyphs in the same packet stay off that stack.
 * Editing View font-root edits (kerning, master guides, …) join via
 * `undoSurfaceAffinity === 'canvas'` only when the item has no originating
 * layer — affinity must not make a layer-origin packet global.
 */
function historyItemTouchesCanvasLayer(item, glyphName, layerId) {
    if (item.originatingGlyphName !== glyphName) {
        return false;
    }
    if (item.originatingLayerId === layerId) {
        return true;
    }
    const touchKey = getLayerTouchKey(glyphName, layerId);
    return !!touchKey && item.touchedLayerKeySet.has(touchKey);
}
function historyItemMatchesCanvasSurface(item, glyphName, layerId) {
    if (item.originatingGlyphName) {
        if (glyphName && layerId) {
            return historyItemTouchesCanvasLayer(item, glyphName, layerId);
        }
        // Text mode (no layer): still reach layer-origin edits for the caret glyph.
        return (!!glyphName &&
            item.originatingGlyphName === glyphName &&
            !!item.originatingLayerId);
    }
    if (item.undoSurfaceAffinity === 'canvas') {
        return true;
    }
    if (glyphName && layerId) {
        return historyItemTouchesCanvasLayer(item, glyphName, layerId);
    }
    return false;
}
function historyItemMatchesSurface(item, surface, options) {
    const glyphName = options.glyphName ?? null;
    const layerId = options.layerId ?? null;
    const historyTargetKey = options.historyTargetKey ?? null;
    switch (surface) {
        case 'canvas': {
            return historyItemMatchesCanvasSurface(item, glyphName, layerId);
        }
        case 'overview': {
            if (item.undoSurfaceAffinity === 'overview') {
                return true;
            }
            if (item.undoSurfaceAffinity === 'canvas') {
                return false;
            }
            if (item.originatingLayerId) {
                return false;
            }
            if (item.historyTargetKeySet.size > 0) {
                return false;
            }
            return item.glyphNameSet.size > 0 || item.undoScope === 'glyph';
        }
        case 'font': {
            if (item.undoSurfaceAffinity === 'canvas' ||
                item.undoSurfaceAffinity === 'overview') {
                return false;
            }
            if (item.historyTargetKeySet.size > 0) {
                return false;
            }
            if (item.originatingLayerId) {
                return false;
            }
            // Master add/remove (and similar) rewrite glyph layers but are
            // font-structural transactions — keep them on the Font surface.
            if (historyItemHasFontStructuralPath(item.entries)) {
                return item.undoScope === 'font';
            }
            if (item.glyphNameSet.size > 0 || item.undoScope === 'glyph') {
                return false;
            }
            return item.undoScope === 'font';
        }
        case 'feature': {
            if (!historyTargetKey) {
                return false;
            }
            return item.historyTargetKeySet.has(historyTargetKey);
        }
        case 'automation':
            return isAutomationSourcedHistoryItem(item);
        default:
            return false;
    }
}
export function buildHistoryStackItems(entries, options) {
    const includeUndone = options?.includeUndone ?? false;
    const surface = inferHistoryUndoSurface({
        glyphName: options?.glyphName,
        layerId: options?.layerId,
        historyTargetKey: options?.historyTargetKey,
        surface: options?.surface
    });
    const state = computeHistoryState(entries);
    return state.orderedItemIds
        .map((itemId) => state.itemsById.get(itemId))
        .filter((item) => !!item)
        .filter((item) => {
        if (surface &&
            !historyItemMatchesSurface(item, surface, {
                glyphName: options?.glyphName,
                layerId: options?.layerId,
                historyTargetKey: options?.historyTargetKey
            })) {
            return false;
        }
        if (!includeUndone && !item.isActive) {
            return false;
        }
        return true;
    })
        .map((item) => stripMutableHistoryItem(item));
}
export function normalizeWindowRoleLabel(windowRoleLabel, windowId) {
    if (windowRoleLabel?.trim()) {
        return windowRoleLabel.trim();
    }
    if (/^(main|primary)$/i.test(windowId)) {
        return 'Main';
    }
    const linkedMatch = windowId.match(/^(?:linked|secondary|remote)[- ]?(\d+)$/i);
    if (linkedMatch) {
        return `Linked ${linkedMatch[1]}`;
    }
    return 'Window';
}
/** Reset the ID counter (for tests). */
export function resetLogCounter() {
    _nextId = 1;
}
/**
 * Derive objectType and objectId from a path array.
 *
 * Path examples:
 *   ["glyphs", "A", "layers", "uuid", "shapes", 0, "nodes", 2, "x"]
 *   ["axes", 0, "tag"]
 *   ["upm"]
 */
export function deriveObjectInfo(path) {
    if (path.length === 0)
        return { objectType: 'font', objectId: '' };
    const first = path[0];
    if (first === 'glyphs' && path.length >= 2) {
        const glyphName = String(path[1]);
        if (path.length === 2)
            return { objectType: 'glyph', objectId: glyphName };
        if (path.length >= 4 && path[2] === 'layers') {
            const layerId = String(path[3]);
            if (path.length === 4)
                return { objectType: 'layer', objectId: layerId };
            if (path.length >= 6 && path[4] === 'shapes') {
                const shapeIdx = String(path[5]);
                if (path.length === 6)
                    return { objectType: 'shape', objectId: shapeIdx };
                if (path.length >= 8 && path[6] === 'nodes')
                    return {
                        objectType: 'node',
                        objectId: `${glyphName}/${layerId}/shape${shapeIdx}/node${path[7]}`
                    };
                // It might be a component or path sub-property
                return { objectType: 'shape', objectId: shapeIdx };
            }
            if (path.length >= 6 && path[4] === 'anchors')
                return {
                    objectType: 'anchor',
                    objectId: `${glyphName}/${layerId}/anchor${path[5]}`
                };
            if (path.length >= 6 && path[4] === 'guides')
                return {
                    objectType: 'guide',
                    objectId: `${glyphName}/${layerId}/guide${path[5]}`
                };
            return { objectType: 'layer', objectId: layerId };
        }
        return { objectType: 'glyph', objectId: glyphName };
    }
    if (first === 'axes' && path.length >= 2)
        return { objectType: 'axis', objectId: String(path[1]) };
    if (first === 'masters' && path.length >= 2)
        return { objectType: 'master', objectId: String(path[1]) };
    if (first === 'instances' && path.length >= 2)
        return { objectType: 'instance', objectId: String(path[1]) };
    if (first === 'features' && path.length >= 2) {
        if (path[1] === 'prefixes' && path.length >= 3) {
            return { objectType: 'prefix', objectId: String(path[2]) };
        }
        if (path[1] === 'classes' && path.length >= 3) {
            return { objectType: 'class', objectId: String(path[2]) };
        }
        if (path[1] === 'features' && path.length >= 3) {
            return { objectType: 'feature', objectId: String(path[2]) };
        }
    }
    return { objectType: 'font', objectId: '' };
}
export function deriveGlyphName(path) {
    if (path[0] === 'glyphs' && typeof path[1] === 'string') {
        return path[1];
    }
    return null;
}
export function deriveLayerId(path) {
    if (path[0] === 'glyphs' &&
        path[2] === 'layers' &&
        typeof path[3] === 'string') {
        return path[3];
    }
    return null;
}
export function deriveGlyphNameFromPath(path) {
    if (!path) {
        return null;
    }
    return deriveGlyphName(getPathSegments(path));
}
export function deriveLayerIdFromPath(path) {
    if (!path) {
        return null;
    }
    return deriveLayerId(getPathSegments(path));
}
export function deriveObjectInfoFromPath(path) {
    return deriveObjectInfo(getPathSegments(path));
}
export function deriveGlyphNamesFromPaths(paths) {
    const glyphNames = new Set();
    for (const path of paths) {
        const glyphName = deriveGlyphNameFromPath(path);
        if (glyphName) {
            glyphNames.add(glyphName);
        }
    }
    return [...glyphNames];
}
export function deriveLayerIdsFromPaths(paths) {
    const layerIds = new Set();
    for (const path of paths) {
        const layerId = deriveLayerIdFromPath(path);
        if (layerId) {
            layerIds.add(layerId);
        }
    }
    return [...layerIds];
}
export function deriveLayerTouchKeysFromPaths(paths) {
    const layerKeys = new Set();
    for (const path of paths) {
        const glyphName = deriveGlyphNameFromPath(path);
        const layerId = deriveLayerIdFromPath(path);
        const layerKey = getLayerTouchKey(glyphName, layerId);
        if (layerKey) {
            layerKeys.add(layerKey);
        }
    }
    return [...layerKeys];
}
export function normalizeChangeLogEntry(entry) {
    const glyphName = deriveGlyphNameFromPath(entry.path);
    const layerId = deriveLayerIdFromPath(entry.path);
    return attachDerivedEntryAccessors({
        id: entry.id,
        timestamp: entry.timestamp,
        transactionDurationMs: entry.transactionDurationMs ?? null,
        windowId: entry.windowId,
        transactionLabel: entry.transactionLabel,
        transactionId: entry.transactionId,
        op: entry.op,
        path: entry.path,
        oldValue: entry.oldValue,
        newValue: entry.newValue,
        replayOldValue: entry.replayOldValue,
        replayNewValue: entry.replayNewValue,
        editSource: entry.editSource ?? null,
        compileChangeSource: entry.compileChangeSource ?? null,
        compileEditType: entry.compileEditType ?? null,
        historySummary: entry.historySummary ?? null,
        promptGroupId: entry.promptGroupId ?? null,
        historyItemId: normalizeHistoryItemId(entry.historyItemId, entry.id),
        historyAction: normalizeHistoryAction(entry.historyAction),
        undoScope: entry.undoScope ?? deriveUndoScope(glyphName, layerId),
        undoSurfaceAffinity: entry.undoSurfaceAffinity === 'canvas' ||
            entry.undoSurfaceAffinity === 'overview'
            ? entry.undoSurfaceAffinity
            : null,
        targetHistoryItemId: entry.targetHistoryItemId ?? null,
        historyTargetType: entry.historyTargetType ?? null,
        historyTargetKey: entry.historyTargetKey ?? null,
        historyTargetLabel: entry.historyTargetLabel ?? null,
        originatingGlyphName: entry.originatingGlyphName ?? null,
        originatingLayerId: entry.originatingLayerId ?? null,
        visualAnchorSide: entry.visualAnchorSide ?? null,
        workerReplayTargets: normalizeWorkerReplayTargets(entry.workerReplayTargets),
        glyphRenames: normalizeGlyphRenames(entry.glyphRenames),
        semanticChangeLogEntries: entry.semanticChangeLogEntries?.map((semanticEntry) => normalizeChangeLogEntry(semanticEntry)),
        windowRoleLabel: normalizeWindowRoleLabel(entry.windowRoleLabel, entry.windowId)
    });
}
